import React from 'react';
import { useLocation, Navigate } from "react-router-dom";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import PaymentsPage from "./pages/PaymentsPage";
import { Expenses } from "./routes/expenses";
import { Products } from "./routes/products";
import { Orders } from "./routes/orders";
import { ChartWarehouse } from "./routes/chart_warehouse";
import { Cod } from "./routes/cod";
import { OrdersUnderwear } from "./routes/underweartest";
import { Obig } from "./routes/obig";
import { PurchasedPrice } from "./routes/purchased_price";
import GoogleSheetData from "./routes/sheet";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ProtectedRoute from "./components/ProtectedRoute";
import useAuth from "./hooks/useAuth";
import { Stocks } from "./routes/stocks";
import { PriceSheet } from "./routes/priceSheet";
import LeadsTicketsEditor from "./pages/Leads";
import TestPage from "./pages/TestPage";
import HomePage from "./pages/HomePage";
import ReportsSummary from "./pages/ReportsSummary";
import ReportsDecomposition from "./pages/ReportsDecomposition";
import ReportsPRTable from "./pages/ReportsPRTable";
import ReportsOPTable from "./pages/ReportsOPTable";
import {Dashboard} from "./routes/dashboard";
import NovaPayPage from "./pages/NovaPayPage";
import AbankPage from "./pages/AbankPage";
import NovaPayBalancesPage from "./pages/NovaPayBalancesPage";
import ReturnProductsPage from "./pages/ReturnProductsPage";
import ReturnsList from "./pages/ReturnsList";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {TouchBackend} from "react-dnd-touch-backend";
import {MultiBackend, MouseTransition, TouchTransition} from "react-dnd-multi-backend";
import TransactionsByTypePage from "./pages/TransactionsByTypePage";
import FacebookPage from "./pages/FacebookPage";
import ProductsMatrix from "./pages/ProductsMatrix";
import MaterialsHubPage from "./pages/MaterialsHubPage";
import ProcessesPage from "./pages/ProcessesPage";
import UsersPage from "./pages/UsersPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import SalariesPage from "./pages/SalariesPage";
import AdminTransactionsConsolePage from "./pages/AdminTransactionsConsolePage";
import WorkSchedulePage from "./pages/WorkSchedulePage";
import ProductNameNormalizeBootstrap from "./context/ProductNameNormalizeBootstrap";
import ProductNameNormalizeSettingsPage from "./pages/ProductNameNormalizeSettingsPage";
import PnlPage from "./pages/PnlPage";
import ShowroomChecklistPage from "./pages/ShowroomChecklistPage";
import TradingPage from "./pages/TradingPage";
import PaymentLinksPage from "./pages/PaymentLinksPage";
import { MANAGER_ROLES, PRODUCTION_BLOCKED_MANAGER_ROLES } from "./helpers/roles";

const DndPipeline = {
    backends: [
        { id: "html5", backend: HTML5Backend, transition: MouseTransition },
        { id: "touch", backend: TouchBackend, options: { enableMouseEvents: true, delayTouchStart: 250 }, preview: true, transition: TouchTransition },
    ],
};

const AppContent = () => {
    const { user, setUser, loading } = useAuth();
    const location = useLocation();

    return (
        <div className="d-flex flex-column flex-fill min-vh-100 w-100" style={{ minHeight: 0 }}>
            <ProductNameNormalizeBootstrap user={user} />
            {location.pathname !== "/return-form" && <Header user={user} setUser={setUser} />}
            <main className="flex-fill d-flex flex-column w-100" style={{ minHeight: 0 }}>
                <div className="flex-grow-1 d-flex flex-column w-100" style={{ minHeight: 0 }}>
                <DndProvider backend={MultiBackend} options={DndPipeline}>
                <Routes>
                    <Route path="/login" element={<Login setUser={setUser} />} />
                    <Route
                        path="/register"
                        element={<ProtectedRoute user={user} loading={loading} requiredRole="admin" />}
                    >
                        <Route index element={<Register />} />
                    </Route>

                    <Route element={<ProtectedRoute user={user} loading={loading} />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/account" element={<AccountSettingsPage />} />
                        <Route path="/payment-links" element={<PaymentLinksPage />} />
                        <Route
                            path="/settings/product-names"
                            element={<ProductNameNormalizeSettingsPage user={user} />}
                        />
                    </Route>

                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                allowedRoles={[
                                    "admin",
                                    "director",
                                    "finance",
                                    ...MANAGER_ROLES,
                                    "seamstress",
                                    "cutter",
                                ]}
                            />
                        }
                    >
                        <Route path="/price" element={<PriceSheet user={user} />} />
                    </Route>

                    <Route element={<ProtectedRoute user={user} loading={loading} excludedRoles={["targetolog"]} />}>
                        <Route path="/schedule" element={<WorkSchedulePage user={user} />} />
                    </Route>

                    <Route element={<ProtectedRoute user={user} loading={loading} allowedRoles={["admin", "director", ...MANAGER_ROLES]} />}>
                        <Route path="/showroom-checklist" element={<ShowroomChecklistPage user={user} />} />
                    </Route>

                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                excludedRoles={["targetolog", "smm", "seamstress", "cutter"]}
                            />
                        }
                    >
                        <Route path="/returns" element={<ReturnProductsPage user={user} />} />
                        <Route path="/products" element={<Products user={user} />} />
                        <Route path="/payments" element={<PaymentsPage user={user} />} />
                        <Route path="/returns-list" element={<ReturnsList user={user}/>} />
                        <Route path="/returns-list/:tab" element={<ReturnsList user={user}/>} />
                    </Route>
                    <Route element={<ProtectedRoute user={user} loading={loading} excludedRoles={["targetolog"]} />}>
                        <Route path="/stocks" element={<Stocks user={user}/>} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                excludedRoles={[
                                    ...PRODUCTION_BLOCKED_MANAGER_ROLES,
                                    "targetolog",
                                    "smm",
                                    "seamstress",
                                    "cutter",
                                ]}
                            />
                        }
                    >
                        <Route path="/materials" element={<Navigate to="/materials/purchases" replace />} />
                        <Route path="/materials/:tab/:sub" element={<MaterialsHubPage user={user} />} />
                        <Route path="/materials/:tab" element={<MaterialsHubPage user={user} />} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                excludedRoles={[...MANAGER_ROLES, "targetolog", "smm", "seamstress", "cutter"]}
                            />
                        }
                    >
                        <Route path="/processes" element={<Navigate to="/processes/planning" replace />} />
                        <Route path="/processes/:subTab" element={<ProcessesPage />} />
                        <Route path="/obig" element={<Obig user={user}/>} />
                        <Route path="/obig/:tab" element={<Obig user={user}/>} />
                        <Route path="/novapay-balances" element={<NovaPayBalancesPage user={user} />} />
                        <Route path="/fb" element={<FacebookPage user={user}/>} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/leads" element={<LeadsTicketsEditor />} />
                        <Route path="/underweartest" element={<OrdersUnderwear />} />
                        <Route path="/cod" element={<Cod />} />
                        <Route path="/charts" element={<ChartWarehouse />} />
                        <Route path="/sheet" element={<GoogleSheetData />} />
                        <Route path="/test" element={<TestPage />} />
                        <Route path="/reports/summary" element={<ReportsSummary />} />
                        <Route path="/reports/decomposition" element={<ReportsDecomposition />} />
                        <Route path="/reports/pr" element={<ReportsPRTable user={user}  />} />
                        <Route path="/reports/op" element={<ReportsOPTable user={user}  />} />
                        <Route
                            element={
                                <ProtectedRoute user={user} loading={loading} requiredRole="admin" />
                            }
                        >
                            <Route path="/salaries/*" element={<SalariesPage user={user} />} />
                        </Route>
                        <Route
                            element={
                                <ProtectedRoute user={user} loading={loading} allowedRoles={["admin", "finance"]} />
                            }
                        >
                            <Route
                                path="/transactions"
                                element={<AdminTransactionsConsolePage user={user} />}
                            />
                            <Route
                                path="/transactions/:tab"
                                element={<AdminTransactionsConsolePage user={user} />}
                            />
                        </Route>
                        <Route
                            element={
                                <ProtectedRoute
                                    user={user}
                                    loading={loading}
                                    allowedRoles={["admin", "finance"]}
                                />
                            }
                        >
                            <Route
                                path="/transactions/business"
                                element={<TransactionsByTypePage user={user} type="business" />}
                            />
                            <Route
                                path="/transactions/cash"
                                element={<TransactionsByTypePage user={user} type="cash" />}
                            />
                        </Route>
                        <Route
                            element={
                                <ProtectedRoute
                                    user={user}
                                    loading={loading}
                                    requiredRole="admin"
                                />
                            }
                        >
                            <Route
                                path="/transactions/personal"
                                element={<TransactionsByTypePage user={user} type="personal" />}
                            />
                        </Route>
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                allowedRoles={["admin", "targetolog"]}
                            />
                        }
                    >
                        <Route path="/matrix" element={<ProductsMatrix />} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                user={user}
                                loading={loading}
                                allowedRoles={["admin", "director"]}
                            />
                        }
                    >
                        <Route path="/orders" element={<Orders user={user} />} />
                    </Route>
                    <Route element={<ProtectedRoute user={user} loading={loading} requiredRole="admin" />}>
                        <Route path="/expenses" element={<Expenses />} />
                        <Route path="/matrix" element={<ProductsMatrix />} />
                        <Route path="/novapay" element={<NovaPayPage />} />
                        <Route path="/abank" element={<AbankPage />} />
                        <Route path="/purchased_price" element={<PurchasedPrice />} />
                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/pnl" element={<PnlPage />} />
                        <Route path="/trading" element={<TradingPage />} />
                    </Route>
                    <Route element={<ProtectedRoute user={user} loading={loading} requiredRole="director" />}>
                    </Route>
                </Routes>
                </DndProvider>
                </div>
            </main>

            <Footer />
        </div>
    );
};


const App = () => (
    <BrowserRouter>
        <AppContent />
    </BrowserRouter>
);

export default App;
